import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  TrendingUp, Euro, Receipt, Percent, Crown, ChevronDown, ChevronUp,
  Search, Banknote, CheckCircle2, Clock,
} from "lucide-react";

const PERIOD_OPTIONS = [
  { value: "all",     label: "Tout" },
  { value: "month",   label: "Ce mois" },
  { value: "quarter", label: "Ce trimestre" },
  { value: "year",    label: "Cette année" },
];

const PLAN_CONFIG = {
  pro:   { label: "Pro",   className: "bg-purple-50 text-purple-700 border-purple-200" },
  basic: { label: "Basic", className: "bg-slate-50 text-slate-600 border-slate-200" },
};

function fmt(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount ?? 0);
}

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function CoachRow({ coach, maxPending, onSelect, selected, onMarkPaid, marking }) {
  const hasPending = coach.coach_payout_pending > 0;

  return (
    <div className={`px-6 py-4 ${selected ? 'bg-blue-50' : ''}`}>
      <div
        className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => onSelect(selected ? null : coach.coach_id)}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {coach.coach_name?.[0]?.toUpperCase() ?? '?'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900 truncate">{coach.coach_name}</p>
            {coach.plan_type && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PLAN_CONFIG[coach.plan_type]?.className ?? ''}`}>
                {coach.plan_type === 'pro' ? <><Crown className="w-3 h-3 inline mr-0.5" />Pro</> : 'Basic'}
              </span>
            )}
            {coach.commission_rate != null && (
              <span className="text-xs text-slate-400">Commission {coach.commission_rate}%</span>
            )}
          </div>
          <p className="text-xs text-slate-400 truncate">{coach.coach_email}</p>
        </div>

        <div className="hidden md:flex items-center gap-3 w-32">
          <MiniBar value={coach.coach_payout_pending} max={maxPending} />
        </div>

        <div className="text-right flex-shrink-0 w-28">
          <p className="text-xs text-slate-500">À reverser</p>
          <p className={`text-sm font-bold ${hasPending ? 'text-amber-700' : 'text-green-700'}`}>
            {fmt(coach.coach_payout_pending)}
          </p>
        </div>

        {selected
          ? <ChevronUp className="w-4 h-4 text-blue-500 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-300 flex-shrink-0" />
        }
      </div>

      {hasPending && (
        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            className="rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            disabled={marking}
            onClick={(e) => { e.stopPropagation(); onMarkPaid(coach.coach_id); }}
          >
            <Banknote className="w-3.5 h-3.5" />
            {marking ? 'Traitement...' : 'Marquer comme reversé'}
          </Button>
        </div>
      )}
    </div>
  );
}

function TransactionList({ transactions }) {
  if (!transactions?.length) {
    return (
      <div className="px-6 pb-4 pt-2 text-sm text-slate-400 border-t border-slate-100">
        Aucune transaction sur cette période
      </div>
    );
  }
  return (
    <div className="border-t border-slate-100 divide-y divide-slate-50">
      {transactions.map(tx => (
        <div key={tx.transaction_id} className="flex items-center gap-4 px-6 py-3 bg-slate-50/60">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${tx.payout_status === 'paid_out' ? 'bg-green-100' : 'bg-amber-100'}`}>
            {tx.payout_status === 'paid_out'
              ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              : <Clock className="w-3.5 h-3.5 text-amber-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-800 truncate">{tx.package_name}</p>
            <p className="text-xs text-slate-400">
              {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              {tx.payout_status === 'paid_out' ? ' · Reversé' : ' · En attente'}
            </p>
          </div>
          <span className="text-xs text-slate-400 flex-shrink-0">
            Commission: {fmt(tx.platform_fee)}
          </span>
          <p className="text-sm font-bold text-emerald-700 flex-shrink-0">{fmt(tx.coach_payout)}</p>
        </div>
      ))}
    </div>
  );
}

function MiniChart({ data }) {
  if (!data?.length) return null;
  const maxVal = Math.max(...data.map(d => d.revenue), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => {
        const heightPct = Math.max(4, (d.revenue / maxVal) * 100);
        const isLast = i === data.length - 1;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div
              className={`w-full rounded-t transition-all ${isLast ? 'bg-blue-500' : 'bg-blue-200 group-hover:bg-blue-400'}`}
              style={{ height: `${heightPct}%` }}
            />
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col items-center z-10 pointer-events-none">
              <div className="bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                {d.month}: {fmt(d.revenue)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachTx, setCoachTx] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [markingCoach, setMarkingCoach] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/revenue?period=${period}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const handleSelectCoach = async (coachId) => {
    setSelectedCoach(coachId);
    if (!coachId) { setCoachTx([]); return; }
    setLoadingTx(true);
    try {
      const r = await api.get(`/admin/revenue?period=${period}&coach_id=${coachId}`);
      setCoachTx(r.data.transactions ?? []);
    } catch {
      toast.error("Erreur de chargement des transactions");
    } finally {
      setLoadingTx(false);
    }
  };

  const handleMarkPaid = async (coachId) => {
    if (!window.confirm("Confirmer le reversement de toutes les transactions en attente pour ce coach ?")) return;
    setMarkingCoach(coachId);
    try {
      const r = await api.post(`/admin/payouts/${coachId}/mark-paid`, {});
      toast.success(`Reversement enregistré : ${fmt(r.data.amount)} (${r.data.marked} transaction(s))`);
      setSelectedCoach(null);
      setCoachTx([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || "Erreur lors du reversement");
    } finally {
      setMarkingCoach(null);
    }
  };

  useEffect(() => { setSelectedCoach(null); setCoachTx([]); }, [period]);

  const filtered = (data?.by_coach ?? []).filter(c =>
    !search.trim() ||
    c.coach_name.toLowerCase().includes(search.toLowerCase()) ||
    c.coach_email.toLowerCase().includes(search.toLowerCase())
  );

  const maxPending = Math.max(...(data?.by_coach ?? []).map(c => c.coach_payout_pending), 1);
  const rates = data?.commission_rates;

  return (
    <div>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Revenus & reversements</h1>
          <p className="text-slate-400 mt-1 text-sm">
            Encaissement plateforme · Commission Basic {rates?.basic ?? 5}% / Pro {rates?.pro ?? 2.5}%
          </p>
        </div>
        <div className="flex gap-2">
          {PERIOD_OPTIONS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 h-9 rounded-full text-sm font-medium transition-colors border ${
                period === p.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-5 space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Encaissé (brut)"
            value={fmt(data?.summary?.total_revenue)}
            icon={Euro}
            color="bg-green-100 text-green-600"
            sub={`${data?.summary?.total_transactions ?? 0} transactions`}
          />
          <StatCard
            label="Commission plateforme"
            value={fmt(data?.summary?.total_platform_fees)}
            icon={Percent}
            color="bg-blue-100 text-blue-600"
            sub="Votre part conservée"
          />
          <StatCard
            label="À reverser aux coachs"
            value={fmt(data?.summary?.total_coach_payout_pending)}
            icon={Banknote}
            color="bg-amber-100 text-amber-700"
            sub="Virements en attente"
          />
          <StatCard
            label="Coachs actifs"
            value={data?.by_coach?.length ?? 0}
            icon={TrendingUp}
            color="bg-purple-100 text-purple-600"
            sub="Ayant généré des revenus"
          />
        </div>
      )}

      {!loading && data?.chart?.length > 0 && (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-5">
            <p className="text-sm font-semibold text-slate-700 mb-4">Encaissements / mois (12 derniers mois)</p>
            <MiniChart data={data.chart} />
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <p className="text-sm font-semibold text-slate-700 flex-1">Reversements par coach</p>
          <div className="relative w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filtrer..."
              className="pl-8 h-8 text-sm bg-white border-slate-200"
            />
          </div>
        </div>

        {loading ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="w-8 h-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-36" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-5 w-20" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">Aucun revenu sur cette période</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(coach => (
              <div key={coach.coach_id}>
                <CoachRow
                  coach={coach}
                  maxPending={maxPending}
                  selected={selectedCoach === coach.coach_id}
                  onSelect={handleSelectCoach}
                  onMarkPaid={handleMarkPaid}
                  marking={markingCoach === coach.coach_id}
                />
                {selectedCoach === coach.coach_id && (
                  loadingTx
                    ? <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60">
                        <Skeleton className="h-8 w-full" />
                      </div>
                    : <TransactionList transactions={coachTx} />
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between rounded-b-xl">
            <p className="text-xs text-slate-500">{filtered.length} coach{filtered.length > 1 ? 's' : ''}</p>
            <p className="text-sm font-bold text-amber-700">
              Total à reverser : {fmt(filtered.reduce((s, c) => s + c.coach_payout_pending, 0))}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}